import type { Task } from "~skillBuilder/utils/types";

import { title } from "../../contentBlocks";

export const AzMrtAbuse = {
  name: "Child Abuse and Neglect",
  uniqueName: "az-mrt-abuse",
  icon: "Lesson",
  durationTimeText: "5 min",
  steps: [
    // Step 1
    {
      nextButtonText: "Start Lesson",
      questions: [
        // Question 1.1
        {
          attribute: "az-mrt.abuse/overview",
          label: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
${title({ text: "Child Abuse and Neglect", durationTimeText: "5 min" })}

To recognize and report abuse effectively, it's essential to understand how Arizona law defines child abuse and neglect. These definitions provide the foundation for identifying behaviors and situations that place children at risk. Abuse can take many forms—physical, sexual, emotional, and neglect. Each has unique warning signs. As a substitute teacher, having clarity on these categories equips you to spot potential concerns and fulfill your duty as a mandated reporter.
`,
          },
        },
      ],
    },
    // Step 2
    {
      questions: [
        // Question 2.1
        {
          attribute: "az-mrt.abuse/definitions",
          label: "Key Definitions",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Arizona law defines several categories of child abuse and neglect. Physical abuse, sexual abuse, emotional abuse, and neglect. Understanding these definitions is crucial for identifying potential cases that require reporting.

<ul style="margin-left: 16px;">
<li>👊 <strong>Physical Abuse</strong> - Non-accidental injury (e.g., bruises, burns, broken bones).</li>
<li>❌ <strong>Sexual Abuse</strong> - Any sexual contact, exploitation, or exposure involving a child.</li>
<li>💔 <strong>Emotional Abuse</strong> - Verbal attacks, threats, or constant criticism that causes serious impairment of a child's emotional development.</li>
<li>🚫 <strong>Neglect</strong> - Failure to meet basic needs like food, shelter, supervision, or medical care.</li>
</ul>

Now that we've outlined the key definitions of child abuse and neglect under Arizona law, let's take a closer look at each category. Understanding the specific indicators, both physical and behavioral, can help you recognize when something may be wrong. As a substitute teacher, you may be one of the first adults to notice these signs, making it vital to know what to look for and how each form of abuse or neglect can present in the classroom.
`,
          },
        },
      ],
    },
    // Step 3
    {
      questions: [
        // Question 3.1
        {
          attribute: "az-mrt.abuse/physical",
          label: "Physical Abuse",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `

Physical abuse involves non-accidental physical injury to a child. As substitute teachers, you should be alert to unexplained physical injuries such as bruises, burns, or fractures. Pay attention to injuries in various stages of healing, which may indicate ongoing abuse. Injuries that form patterns or resemble objects like belt marks or hand prints are particularly concerning. Be aware when a child's explanation for an injury seems inconsistent with the type or severity of the injury. Behavioral indicators include fear of adults or specific individuals, and wearing inappropriate clothing (like long sleeves in hot weather) to cover injuries.

**Physical Abuse Indicators:**

<ul style="margin-left: 16px;">
<li>🤕 <strong>Unexplained Injuries</strong> - Bruises, burns, or fractures with no clear explanation—or vague, inconsistent stories.</li>
<li>⏳ <strong>Multiple Stages of Healing</strong> - Old and new bruises appearing at the same time may suggest repeated harm.</li>
<li>🧵 <strong>Patterned Marks</strong> - Injuries shaped like objects (e.g., belt buckles, cords, handprints) can be signs of deliberate harm.</li>
<li>❓ <strong>Inconsistent Explanations</strong> - When a child's story doesn't match the injury—or changes over time—it may signal abuse.</li>
<li>😟 <strong>Fear-Based Behavior</strong> - Flinching around adults, being unusually watchful, or scared of going home.</li>
<li>🧥 <strong>Inappropriate Clothing for the Weather</strong> - Long sleeves or pants in hot weather might be used to hide injuries.</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 4
    {
      questions: [
        // Question 4.1
        {
          attribute: "az-mrt.abuse/sexual",
          label: "Sexual Abuse",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `

Sexual abuse can be especially difficult for children to disclose. Physical indicators may include difficulty walking or sitting, and torn or stained clothing. Behavioral indicators can include age-inappropriate sexual knowledge or behavior, fear of specific people or places, or sudden changes in mood or conduct.

Children may make direct or indirect verbal disclosures, or show reluctance to change clothes for activities such as gym class. They might also exhibit withdrawal, excessive fantasy, or regressive (infant-like) behaviors.

It's important to remember that children rarely fabricate reports of sexual abuse. Any disclosure or suspicion should be taken seriously and reported immediately in accordance with mandated reporting laws and organizational policy.

**Sexual Abuse Indicators:**

<ul style="margin-left: 16px;">
<li>🚶‍️ <strong>Physical Discomfort</strong> - Difficulty walking or sitting due to pain or discomfort.</li>
<li>🧠 <strong>Age-Inappropriate Sexual Behavior or Knowledge</strong> - Using language or acting out behaviors far beyond what's typical for their age.</li>
<li>😨 <strong>Fear of Specific People or Places</strong> - Sudden fear of being around a certain adult, or strong resistance to returning to school or home.</li>
<li>🗣️ <strong>Verbal Hints or Disclosures</strong> - A child might say something indirect, like "I don't like being alone with ___" or "something bad happened."</li>
<li>🚫 <strong>Avoidance of Changing Clothes</strong> - Refusing to dress for gym or swimming could signal body-related trauma or shame.</li>
<li>🌫️ <strong>Behavioral Regression</strong> - Acting much younger than their age (thumb-sucking, urinary accident) or escaping into fantasy worlds.</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 5
    {
      nextButtonText: "Next Lesson",
      questions: [
        // Question 5.1
        {
          attribute: "az-mrt.abuse/emotional",
          label: "Emotional Abuse and Neglect",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `

Emotional abuse and neglect can be harder to detect but are equally harmful. Emotional abuse may manifest as delayed emotional development, extreme behavior patterns, self-destructive tendencies, or sleep disorders.

**Emotional Abuse Indicators:**

<ul style="margin-left: 16px;">
<li>🧠 <strong>Delayed Emotional Development</strong> - The child may struggle with social skills, show little emotion, or seem disconnected from peers.</li>
<li>🔁 <strong>Extreme Behavior</strong> - Watch for kids who are overly compliant, constantly seeking approval, or, conversely, unusually aggressive or defiant.</li>
<li>⚠️ <strong>Self-Destructive Behavior</strong> - Actions like head-banging, self-harming, or talking about wanting to disappear may signal internal distress.</li>
<li>🌙 <strong>Sleep Disorders</strong> - Reports of nightmares, falling asleep in class, or difficulty staying awake may indicate emotional turmoil at home.</li>
</ul>

**Neglect Indicators:**

<ul style="margin-left: 16px;">
<li>🍽️ <strong>Chronic Hunger or Fatigue</strong> - Students who frequently ask for extra snacks or seem constantly tired may not be receiving basic care.</li>
<li>🧼 <strong>Poor Hygiene</strong> - Dirty clothing, strong body odor, or untreated lice can be red flags for neglect.</li>
<li>🩺 <strong>Unmet Medical or Dental Needs</strong> - Obvious dental decay, untreated injuries, or lack of eyeglasses when needed may suggest inadequate care.</li>
<li>🕒 <strong>Lack of Supervision or Frequent Absences</strong> - Children who are dropped off extremely early, picked up late, or consistently miss school may not have adequate supervision.</li>
<li>🧥 <strong>Inappropriate Clothing for Weather</strong> - Wearing sandals in winter or no coat on cold days could reflect neglect, not just forgetfulness.</li>
</ul>
`,
          },
        },
      ],
    },
  ],
} as const satisfies Task;
